import _ from 'lodash'
import axios from 'axios'
// Services
import UsersService from 'src/services/users'
import PermissionsService from 'src/services/permissions'
import LocationsService from 'src/services/locations'
// Stores
import { useLocationsStore, useRolesStore, usePermissionsStore } from 'src/stores/index.js'
import API from 'src/api'
// Utils
import { getFCMToken } from '@/utils/fcm'
import { readBootCurrentCache, writeBootCurrentCache, BOOT_FETCH_LOCK_NAME } from './bootCache'

const getOrganizationAdminUsers = (data, roleId, locationIds, locationsStore, rolesStore) => {
  const roleDef = rolesStore.roles.find(currentRole => currentRole.id === roleId)
  const roleFranchiseIds = Array.isArray(roleDef?.franchise_role_ids) ? roleDef.franchise_role_ids : []

  return data.filter(user => {
    return user.roles.some(role => {
      return role.role_id === roleId && locationIds.some(loc => {
        if (loc !== role.location_id) return false
        const locationFranchiseIds = locationsStore.keyedById[loc]?.franchise_ids
        if (!Array.isArray(locationFranchiseIds)) return false
        return locationFranchiseIds.some(franchiseId => roleFranchiseIds.includes(franchiseId))
      })
    })
  })
}

const getNonAdminUsers = (data, roleId, locationIds, rolesStore, rolesWithHierarchy, userFranchisesAdmin, userFranchisesSupervisor) => {
  const availableSuperiorRoles = rolesWithHierarchy
    .filter(role => locationIds.find(id => id === role.location_id))
    .filter(selectRole => {
      const finded = rolesStore.getUniqueRolesByHierarchy.find(roleHier => roleHier.id === selectRole.id)
      if (finded) {
        return rolesStore.getUniqueRolesByHierarchy.find(roleHier => roleHier.id === selectRole.id)
          .subRoles.some(sub => sub.id === roleId)
      }
    })
    .filter((role, index, self) => index === self.findIndex(o => o.location_id === role.location_id))

  const tops = new Set([...userFranchisesAdmin, ...userFranchisesSupervisor])

  const users = data.filter(user => {
    return user.roles.some(userRole => {
      const roleFranchise = userRole.franchise_id
      const roleLocation = userRole.location_id

      return availableSuperiorRoles.some(avRoles => avRoles.location_id === roleLocation && avRoles.franchise_role_id === roleFranchise)
    })
  })

  const allFranchUsers = data.filter(user => {
    return user.roles.some(userRole => {
      const validTop = tops.has(userRole.franchise_id)
      return locationIds.some(loc => loc === userRole.location_id && userRole.role_id === roleId && validTop)
    })
  })

  return [...allFranchUsers, ...users].reduce((uniqueUser, role) => {
    if (!uniqueUser.some(r => r.id === role.id)) uniqueUser.push(role)
    return uniqueUser
  }, [])
}

export default {
  // Registra el token FCM del dispositivo contra el usuario. Se llama en el login Y en cada
  // arranque autenticado a proposito: el backend borra la fila cuando FCM responde NotFound
  // (FirebaseService::handleInvalidToken), asi que sin un re-registro periodico un usuario que
  // rota token queda con cero dispositivos y sin push hasta que vuelva a iniciar sesion.
  // Devuelve el token registrado, o null si la plataforma no entrega uno (web).
  async registerDevice (userId) {
    const token = await getFCMToken()
    if (!token) return null

    await UsersService.registerDevice({ user_id: userId, token })
    return token
  },
  unregisterDevice (token) {
    UsersService.unregisterDevice(token)
  },
  // Extraído de fetchCurrent() para que fetchCurrentForBoot() (abajo) tenga acceso a los
  // `permissions` CRUDOS del backend (setModulesPermissions espera el shape crudo, no el ya
  // transformado que queda en el store) -- necesarios para escribir la cache entre pestañas.
  // fetchCurrent() sigue exactamente igual desde afuera: mismos efectos, mismo valor de retorno.
  async fetchCurrentAndPermissions () {
    const user = await UsersService.fetchCurrent()
    this.current = user

    const permissionsStore = usePermissionsStore()
    const permissions = await PermissionsService.getAdminPermissions()

    permissionsStore.setModulesPermissions(permissions)

    return { user, permissions }
  },
  async fetchCurrent () {
    const { user } = await this.fetchCurrentAndPermissions()
    return user
  },
  // Boot con coordinacion entre pestañas (WDUR-tabs-blancas): N pestañas abiertas casi
  // simultaneas disparaban N x 2 requests (usuario + permisos) contra el mismo backend, agravando
  // la contencion que ya tumbaba pestañas en blanco. Cache de corta duracion en localStorage
  // (bootCache.js) + Web Lock como fallback cuando la cache aun no esta escrita: solo la pestaña
  // que gana el lock hace el fetch real, el resto reusa su resultado. Doble chequeo de cache
  // (antes Y despues del lock) porque una pestaña pudo quedar en cola del lock mientras otra ya
  // escribia el resultado fresco.
  //
  // Solo para boot.js: cualquier otro caller que necesite datos frescos garantizados (p. ej.
  // AdminIndex.vue) debe seguir usando fetchCurrent() a secas, sin pasar por esta cache.
  async fetchCurrentForBoot () {
    const applyCache = (entry) => {
      this.current = entry.user
      usePermissionsStore().setModulesPermissions(entry.permissions)
      return entry.user
    }

    const cached = readBootCurrentCache()
    if (cached) return applyCache(cached)

    const hasWebLocks = typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function'
    if (!hasWebLocks) {
      const { user, permissions } = await this.fetchCurrentAndPermissions()
      writeBootCurrentCache(user, permissions)
      return user
    }

    // Sin `ifAvailable`: las pestañas que no ganan la carrera se QUEDAN EN COLA (no se saltan el
    // trabajo). Cuando les toca su turno, re-chequean la cache -- si quien iba antes ya la escribió
    // mientras esperaban, la usan y resuelven casi gratis en vez de duplicar la red.
    return navigator.locks.request(BOOT_FETCH_LOCK_NAME, async () => {
      const cachedAfterLock = readBootCurrentCache()
      if (cachedAfterLock) return applyCache(cachedAfterLock)

      const { user, permissions } = await this.fetchCurrentAndPermissions()
      writeBootCurrentCache(user, permissions)
      return user
    })
  },
  fetchByIds (ids) {
    this.users = []
    this.status = 'loading'

    const promise = UsersService.fetchByIds(ids)

    promise.then(data => {
      this.users = data
      this.status = 'success'
    })

    return promise
  },
  usersByIdsAndRoleId (payload) {
    this.users = []
    this.status = 'loading'

    const promise = UsersService.usersByIdsAndRoleId(payload)
    
    promise.then(data => {
      this.users = data
      this.status = 'success'
    })

    return promise
  },
  async fetchByRoleInside({ roleId, inState }) {
    this.status = 'loading'
    if (inState) this.users = []

    let users = []
    await UsersService.fetchByRolesGraph(roleId).then(data => {
      if (inState) this.users = data
      users = data
      this.status = 'success'
    })

    return users
  },
  fetchByRole (roleId) {
    this.users = []
    this.status = 'loading'

    const promise = UsersService.fetchByRoles(roleId)
    promise.then(data => {
      this.users = data
      this.status = 'success'
    })

    return promise
  },
  fetchByHierarchyRoles (payload) {
    this.status = 'loading'

    const locationsStore = useLocationsStore()
    const rolesStore = useRolesStore()
    const { locations, role_id } = payload
    const locationIds = locations[0]

    const promise = UsersService.fetchByRolesAndLocations(payload)
    promise.then(data => {
      if (this.currentIsOrganizationAdmin) {
        const users = getOrganizationAdminUsers(data, role_id, locationIds, locationsStore, rolesStore)

        this.users = users
        this.status = 'success'
        return users
      } else {
        const finalUsers = getNonAdminUsers(
          data,
          role_id,
          locationIds,
          rolesStore,
          this.getRolesWithHierarchy,
          this.userFranchisesAdmin,
          this.userFranchisesSupervisor
        )
        this.users = finalUsers
        this.status = 'success'
        return finalUsers
      }
    })

    return promise
  },
  fetchByCreatorRole (roleId) {
    this.status = 'loading'
    const promise = UsersService.fetchByRoles(roleId)

    promise.then(data => {
      this.creator_users = data
      this.status = 'success'
    })

    return promise
  },
  fetchByRoleAndLocations (payload) {
    this.status = 'loading'
    const promise = UsersService.fetchByRolesAndLocations(payload)

    promise.then(data => {
      this.users = []
      this.users = data
      this.status = 'success'
    })

    return promise
  },
  fetchByCreatorRoleAndLocations (payload) {
    this.status = 'loading'
    const promise = UsersService.fetchByRolesAndLocations(payload)

    promise.then(data => {
      this.creator_users = data
      this.status = 'success'
    })

    return promise
  },
  fetchPaginated (payload = {}) {
    const page = payload.page || 1
    if (page === 1 && this.users.length > 0) {
      this.users = []
      this.status = 'loading'
    }
    const promise = UsersService.fetchPaginated(payload)

    promise
      .then(data => {
        if (Array.isArray(data)) this.users = []
        else this.users = this.users.concat(data.data)

        if (data.data.length > 0) {
          this.pagination = {
            count: data.to,
            current_page: data.current_page,
            links: [],
            per_page: data.per_page,
            total: data.total,
            total_pages: data.last_page,
          }
        }
        this.status = 'success'
      })
      .catch((err) => {
        console.error(err)
      })

    return promise
  },
  remove (id) {
    this.users = this.users.filter((user) => user.id !== id)
    this.status = 'success'
  },
  fetchLocations (payload) {
    const promise = LocationsService.fetchPaginated(payload)

    promise.then(data => {
      this.locations = data.data
    })

    return promise
  },
  fetchMails () {
    const promise = UsersService.getMails()

    promise.then(data => {
      this.mails = data
    })

    return promise
  },
  searchMails (search) {
    const promise = UsersService.searchMails(search)

    promise.then(data => {
      this.mails = _.map(data[0], 'email')
    })
    return promise
  },
  disable (user) {
    const payload = {
      id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      franchise_id: user.franchise?.id,
      organization_id: user.organization_id,
      franchise_organization_id: user.organization_id,
      is_basic: user.is_basic,
      status: !user.status,
      change_state: true,
    }
    const promise = UsersService.update(user.id, payload)

    promise.then(() => {
      this.users = this.users.map((us) => {
        if (us.id === user.id) us.status = us.status === 1 ? 0 : 1

        return us
      })
    })

    return promise
  },

  resetPassword (payload) {
    const promise = UsersService.resetPassword(payload)

    promise.then(data => {
      this.pass = data
    })

    return promise
  },
  revokeBiometric (id) {
    return UsersService.revokeBiometric(id)
  },
  revokeSessions (id) {
    return UsersService.revokeSessions(id)
  },
  fetchSessionSecurity (id) {
    return UsersService.fetchSessionSecurity(id)
  },
  uploadTemplateUser (payload) {
    const promise = UsersService.uploadTemplateUser(payload)

    promise
      .then(data => {
        this.message = 'positive'
        this.setInfo = data
      })
      .catch((err) => {
        this.message = 'negative'
        this.setInfo = err
        console.error(err)
      })

    return promise
  },
  setCurrent (user) {
    this.current = user
  },
  setUsers (users) {
    this.users = users
  },
  setPagination (pagination) {
    this.pagination = pagination
  },
  updateList (user) {
    const item = this.users.find((item) => item.id === user.id)

    if (item) Object.assign(item, user)
    else this.users.unshift(user)
  },
  fetchMetabaseAccess () {
    const promise = axios.get(`${API.GRAPHQL_URL}userMetabase`)
      .then(response => { this.hasMetabaseAccess = response.data.user?.hasMetabaseAccess ?? false })
      .catch(error => {
        console.error('Error obteniendo acceso a Metabase', error)
        this.hasMetabaseAccess = false
      })
    return promise
  }
}
